import { createFileRoute, Link } from "@tanstack/react-router";
import { landingsByKind, type LandingKind } from "@/lib/seo-landing";
import { absoluteUrl, socialImageMeta, breadcrumbLd } from "@/lib/seo";
import { LOCALES, LOCALE_PREFIX, SITE_LOCALE_HREFLANG } from "@/i18n/config";
import { useTranslation } from "@/i18n";

const KIND_ORDER: LandingKind[] = ["game", "grade", "character", "language", "format"];

export const Route = createFileRoute("/collections/")({
  head: () => {
    const path = "/collections";
    const url = absoluteUrl(path);
    const title = "Collections | Trading Card Auction Categories — BidNova";
    const description = "Browse curated trading card auction collections by game, grade, character, language and format on BidNova Auctions.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        ...socialImageMeta(),
      ],
      links: [
        { rel: "canonical", href: url },
        ...LOCALES.map((loc) => ({
          rel: "alternate",
          hrefLang: SITE_LOCALE_HREFLANG[loc],
          href: `https://bidnovaauctions.uk${LOCALE_PREFIX[loc]}${path}`,
        })),
        { rel: "alternate", hrefLang: "x-default", href: `https://bidnovaauctions.uk${path}` },
      ],
      scripts: [
        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Auctions", path: "/auctions" },
          { name: "Collections", path },
        ]),
      ],
    };
  },
  component: CollectionsHubPage,
});

function CollectionsHubPage() {
  const { t } = useTranslation("landing");
  const grouped = landingsByKind();

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
      <nav aria-label="Breadcrumb" className="mb-6 text-xs text-muted-foreground">
        <ol className="flex flex-wrap items-center gap-1">
          <li><Link to="/" className="hover:text-foreground">{t("common.breadcrumbHome", "Home")}</Link></li>
          <li aria-hidden="true">/</li>
          <li><Link to="/auctions" className="hover:text-foreground">{t("common.breadcrumbAuctions", "Auctions")}</Link></li>
          <li aria-hidden="true">/</li>
          <li aria-current="page" className="text-foreground">{t("common.breadcrumbCollections", "Collections")}</li>
        </ol>
      </nav>

      <header className="mb-10">
        <p className="text-xs uppercase tracking-widest text-gold">{t("common.breadcrumbCollections", "Collections")}</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl">{t("hub.h1", "Collections")}</h1>
        <p className="mt-3 max-w-3xl text-muted-foreground">
          {t("hub.intro", "Explore curated collections of live trading card auctions, grouped by game, grade, character, language and format.")}
        </p>
      </header>

      <div className="space-y-12">
        {KIND_ORDER.map((kind) => {
          const items = grouped[kind];
          if (!items.length) return null;
          return (
            <section key={kind} aria-labelledby={`kind-${kind}`}>
              <h2 id={`kind-${kind}`} className="font-display text-2xl">
                {t(`hub.kinds.${kind}`, kind)}
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {items.map((d) => (
                  <Link
                    key={d.slug}
                    to="/collections/$slug"
                    params={{ slug: d.slug }}
                    className="rounded-full border border-gold/30 bg-gold/5 px-4 py-2 text-sm hover:bg-gold/10"
                  >
                    {d.label}
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
